import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AUTOCOMPLETE_OPTIONS,
  DEFAULT_PLACE_RESULT
} from '@/sections/address/components/AddressForm/Autocomplete/constants';
import type {
  IAutocomplete,
  PlacesAutocompleteResult
} from '@/sections/address/components/AddressForm/Autocomplete/interfaces';
import { loadGoogleMapsApi } from '@/sections/address/components/AddressForm/Autocomplete/mapsApiLoader';
import { getAddressComponent } from '@/sections/address/components/AddressForm/Autocomplete/utils';
import useRegions from '@/sections/region/hooks/useRegions';

type TOnPlaceSelect = (place: PlacesAutocompleteResult) => void;

const usePlacesAutocomplete = (onPlaceSelect?: TOnPlaceSelect) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<IAutocomplete | null>(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [placeResult, setPlaceResult] = useState(DEFAULT_PLACE_RESULT);

  const { data: regions } = useRegions();

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();

    if (place?.geometry === undefined || place?.geometry === null) return;

    const addressComponent = getAddressComponent(place, regions);

    if (place && onPlaceSelect && addressComponent?.addressComplete) {
      onPlaceSelect(addressComponent.addressComplete);
    }

    setPlaceResult(addressComponent?.addressComplete ?? DEFAULT_PLACE_RESULT);
  }, [onPlaceSelect, regions]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsApi().then(({ Autocomplete }) => {
      if (!isMounted || !inputRef.current) return;

      setIsLoaded(true);
      autocompleteRef.current = new Autocomplete(
        inputRef.current,
        AUTOCOMPLETE_OPTIONS
      );

      autocompleteRef.current.addListener('place_changed', handlePlaceChanged);
    });

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [handlePlaceChanged, inputRef]);

  return {
    inputRef,
    isLoaded,
    placeResult
  };
};

export default usePlacesAutocomplete;
